/**
 * What to call a booking on screen.
 *
 * OTA feeds export placeholder titles that say nothing useful and, worse, say
 * the wrong thing. Booking.com labels EVERY exported event "CLOSED - Not
 * available" — real guest reservations included, for guest-privacy reasons —
 * so a genuine booking showed up on the calendar looking like a closed-off
 * date. Airbnb and MMT use "(Not available)" variants with the same problem.
 *
 * The raw text is still stored on the booking and must stay that way: the
 * sync's own host-closure classification matches on it
 * (HOST_CLOSURE_SUMMARY_PATTERN in icalSyncService.js). This is a display
 * concern only, so it lives here rather than changing what is saved.
 *
 * Order of preference: the guest's real name, then a meaningful title the
 * feed or an admin actually wrote (a manual block's reason, say), and only
 * then a generated description of what the entry is and where it came from.
 */
const PLACEHOLDER = [
  /not\s*available/i,   // Booking.com, Airbnb, MMT closures — and Booking.com's real bookings
  /^closed\b/i,         // "CLOSED - Not available"
  /^reserved$/i,        // Airbnb's generic reservation title
  /^blocked$/i,         // our own default when no reason was given
];

export function bookingLabel(booking, fallback = "Reserved") {
  const name = booking?.guest?.fullNames?.[0];
  if (name) return name;

  const summary = String(booking?.iCalSummary || "").trim();
  const source  = booking?.source || "";
  const fromOta = source && source !== "Manual" && source !== "Website";

  // A title someone actually chose — a manual block's reason ("Maintenance"),
  // or a feed that exports something real — is better than anything we'd make up.
  const isPlaceholder = !summary || PLACEHOLDER.some(re => re.test(summary));
  if (!isPlaceholder) return summary;

  if (booking?.status === "Blocked") {
    return fromOta ? `Blocked on ${source}` : "Blocked";
  }
  // A real reservation the feed wouldn't name. Saying which channel it came
  // from is the most useful thing left to say about it.
  return fromOta ? `${source} booking` : fallback;
}
